import React, { useState } from "react";
import axios from "axios";
import { Stethoscope, Loader2, AlertTriangle, ShieldCheck, Activity } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const severityMap = {
  "alacsony": { color: "#A3A3A3", label: "Alacsony" },
  "közepes": { color: "#FFD700", label: "Közepes" },
  "magas": { color: "#FF8C00", label: "Magas" },
  "kritikus": { color: "#FF3333", label: "Kritikus" },
};

export default function AIDiagnosis() {
  const [tunetek, setTunetek] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const examples = [
    "Néha leoltja a biztosítékot mosáskor",
    "Égett szag jön a konnektorból",
    "Villog a lámpa amikor más készülék bekapcsol",
    "Áramütést éreztem a mosógép házából",
  ];

  const submit = async () => {
    if (!tunetek.trim()) {
      setError("Kérjük írja le a tüneteket.");
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/ai/diagnose`, { tunetek });
      setResult(data);
    } catch (e) {
      setError("Hiba a diagnózis során. Próbálja újra.");
    } finally {
      setLoading(false);
    }
  };

  const sev = result ? severityMap[result.sulyossag] || severityMap["közepes"] : null;

  return (
    <div data-testid="ai-diagnosis" className="border-2 border-[#262626] bg-black p-5 md:p-7">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 bg-[#FFD700] flex items-center justify-center">
          <Stethoscope className="w-5 h-5 text-black" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-heading uppercase text-lg leading-none">Hibadiagnosztika</div>
          <div className="font-mono text-xs text-[#A3A3A3] mt-1">Írd le a tüneteket — AI megmondja a valószínű okot</div>
        </div>
      </div>

      <label className="font-mono text-xs uppercase tracking-widest text-[#A3A3A3] block mb-2">
        Tünetek leírása
      </label>
      <textarea
        data-testid="diag-input"
        className="input-brutal min-h-[120px] mb-3"
        placeholder="pl. mosáskor leoltja a biztosítékot, főleg centrifugáláskor..."
        value={tunetek}
        onChange={(e) => setTunetek(e.target.value)}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {examples.map((ex, i) => (
          <button
            key={i}
            onClick={() => setTunetek(ex)}
            data-testid={`diag-example-${i}`}
            className="font-mono text-[11px] uppercase tracking-wider px-2 py-1 border border-[#262626] text-[#A3A3A3] hover:border-[#FFD700] hover:text-[#FFD700] transition-colors"
          >
            + {ex}
          </button>
        ))}
      </div>

      {error && <div data-testid="diag-error" className="font-mono text-sm text-[#FF3333] mb-3">{error}</div>}

      <button
        data-testid="diag-submit"
        onClick={submit}
        disabled={loading}
        className="btn-primary w-full justify-center disabled:opacity-60"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Elemzem...</> : <><Activity className="w-4 h-4" /> Diagnózis indítása</>}
      </button>

      {result && sev && (
        <div data-testid="diag-result" className="mt-6 space-y-4">
          {/* Severity banner */}
          <div
            className="p-4 border-2 flex items-center gap-3"
            style={{ borderColor: sev.color, background: `${sev.color}15` }}
          >
            {result.sulyossag === "kritikus" ? <AlertTriangle className="w-7 h-7" style={{ color: sev.color }} /> : <Activity className="w-7 h-7" style={{ color: sev.color }} />}
            <div>
              <div className="font-mono text-xs uppercase tracking-widest" style={{ color: sev.color }}>
                Súlyosság
              </div>
              <div className="font-heading uppercase text-2xl" style={{ color: sev.color }}>
                {sev.label}
              </div>
            </div>
          </div>

          <div className="border-2 border-[#FFD700] p-5 bg-[#FFD700]/5">
            <div className="font-mono text-xs uppercase tracking-widest text-[#FFD700] mb-2">
              Valószínű ok
            </div>
            <p className="text-white leading-relaxed">{result.valoszinu_ok}</p>
          </div>

          <div className="border border-[#262626] p-5">
            <div className="font-mono text-xs uppercase tracking-widest text-[#A3A3A3] mb-3">
              Azonnali lépések
            </div>
            <ol className="space-y-2 font-mono text-sm">
              {(result.azonnali_lepesek || []).map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-[#FFD700] font-bold">{String(i + 1).padStart(2, "0")}.</span>
                  <span className="text-[#F5F5F5]">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="border border-[#262626] p-5 flex items-start gap-3">
            <ShieldCheck className={`w-6 h-6 shrink-0 ${result.biztonsagos_hazilag ? "text-[#FFD700]" : "text-[#FF3333]"}`} />
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-[#A3A3A3]">
                Házilag biztonságos?
              </div>
              <div className={`font-heading uppercase text-lg ${result.biztonsagos_hazilag ? "text-[#FFD700]" : "text-[#FF3333]"}`}>
                {result.biztonsagos_hazilag ? "Igen, óvatosan" : "NEM — szerelő szükséges"}
              </div>
              <p className="text-sm text-[#A3A3A3] mt-2 font-mono leading-relaxed">{result.javaslat}</p>
            </div>
          </div>

          <a href="#kapcsolat" data-testid="diag-to-contact" className="btn-danger w-full justify-center">
            Vészhelyzeti kiszállás kérése
          </a>
        </div>
      )}
    </div>
  );
}
